import { CreateItemPayload } from "@/types/payload/create-item-payload"; // 생성된 아이템 페이로드
import { Button, TextInput } from "flowbite-react"; // UI
import React from "react";
import { ButtonColor } from "../../common/enum/button-color";
import { ImageCarouselInput } from "../carousel/ImageCarouselInput"; // image input 시, 캐러셀 사용?
import { ItemCategoryDropdown } from "../dropdown/ItemCategoryDropdown";
import { SearchOutlineIcon } from "../icon/SearchOutlineIcon";
import { TextInputLabel } from "../label/TextInputLabel";
import { Body14Regular } from "../typography/Body14Regular"; // 텍스트 스타일 지정
import { Footnote12Description } from "../typography/Footnote12Description"; // 텍스트 스타일 지정
import { MemoInput } from "../memo/MemoInput";
import { CustomEditor } from "../tiptap/CustomEditor";
import { getTextLength } from "@/common/util/jsonUtils";
import { CustomHelperText } from "@/components/CustomHelperText";

interface Props {
  createItemPayload: CreateItemPayload;
  updateCategoryId: (categoryId: number) => void;
  handleFileChange: (files: File[]) => void;
  handleChange: (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => void;
  handleCatalogChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  handleOpenItemCodeModal: () => void;
  handleMemoChange: (index: number, value: string) => void;
  handleAddMemo: () => void;
  handleRemoveMemo: (index: number) => void;
  isUrlValid: boolean;
}

export const ItemCreatePanel: React.FC<Props> = ({
  createItemPayload,
  updateCategoryId,
  handleFileChange,
  handleChange,
  handleCatalogChange,
  handleOpenItemCodeModal,
  handleMemoChange,
  handleAddMemo,
  handleRemoveMemo,
  isUrlValid,
}) => {
  const memos = createItemPayload.memos || [];

  return (
    <div className="flex bg-primary-white rounded-[8px] p-[24px]">
      <ImageCarouselInput
        files={createItemPayload.files}
        onFileChange={handleFileChange}
      />
      <div className="flex flex-col ml-[20px]">
        <div className="flex items-center mb-[16px] gap-[16px]">
          <div className="flex-1">
            <TextInputLabel required>상품 카테고리</TextInputLabel>
            <div className="mb-16px"></div>
            <ItemCategoryDropdown updateCategoryId={updateCategoryId} />
          </div>
          <div className="flex-1">
            <div className="text-semantic-text-heading mb-[4px]">
              <TextInputLabel required>ERP 코드연동</TextInputLabel>
            </div>
            <div className="flex gap-[8px] items-center">
              <Button
                onClick={handleOpenItemCodeModal}
                color={
                  createItemPayload.erpCode
                    ? ButtonColor.CUSTOM_GRAY
                    : ButtonColor.PRIMARY
                }
                className="w-[100px] border"
              >
                <div className="flex items-center gap-[8px]">
                  <Body14Regular
                    className={`${createItemPayload.erpCode
                      ? "text-semantic-text-disabled"
                      : "text-white"
                      }`}
                  >
                    코드 검색
                  </Body14Regular>
                  <SearchOutlineIcon
                    size={16}
                    color={
                      createItemPayload.erpCode
                        ? "text-semantic-text-disabled"
                        : "text-white"
                    }
                  />
                </div>
              </Button>
              <Footnote12Description className="text-semantic-text-secondary">
                {createItemPayload.erpCode &&
                  `코드명: ${createItemPayload.erpCode}`}
              </Footnote12Description>
            </div>
          </div>
        </div>
        <div className="flex my-[16px] gap-[16px]">
          <div className="flex flex-col">
            <TextInputLabel required>상품명</TextInputLabel>
            <TextInput
              required
              className="w-[398px]"
              name="name"
              placeholder="상품명을 입력해주세요"
              value={createItemPayload.name}
              onChange={handleChange}
            />
          </div>
          <div className="flex flex-col">
            <TextInputLabel required>모델명</TextInputLabel>
            <TextInput
              required
              className="w-[398px]"
              name="modelName"
              placeholder="모델명을 입력해주세요"
              value={createItemPayload.modelName}
              onChange={handleChange}
            />
          </div>
        </div>
        <div className="flex mb-[24px] gap-[16px]">
          <div className="flex flex-col">
            <TextInputLabel>연결 링크</TextInputLabel>
            <div>
              <TextInput
                name="link"
                value={createItemPayload.link}
                onChange={handleChange}
                className={`w-[398px] ${!isUrlValid ? "border-red-500" : ""}`}
                placeholder="더보기 버튼으로 연결할 링크를 입력해주세요"
              />
              {!isUrlValid && (
                <CustomHelperText
                  isError={true}
                  text="https://를 포함해서 입력해주세요."
                />
              )}
            </div>
          </div>
          {/* <div className="flex flex-col">
            <div className="text-semantic-text-heading mt-[8px] mb-[8px]">
              <TextInputLabel required>
                공식 홈페이지의 제품 카탈로그에도 등록하시겠어요?
              </TextInputLabel>
            </div>
            <fieldset className="flex max-w-md gap-4">
              <div className="flex items-center gap-2">
                <Radio
                  id="catalog-yes"
                  name="catalog"
                  value="YES"
                  checked={createItemPayload.isCataloged}
                  onChange={handleCatalogChange}
                />
                <Label htmlFor="catalog-yes">카탈로그 등록</Label>
              </div>
              <div className="flex items-center gap-2">
                <Radio
                  id="catalog-no"
                  name="catalog"
                  value="NO"
                  checked={!createItemPayload.isCataloged}
                  onChange={handleCatalogChange}
                />
                <Label htmlFor="catalog-no">등록 안함 (마스터만 등록)</Label>
              </div>
            </fieldset>
          </div> */}
          <div className="flex flex-col">
            <div className="text-semantic-text-heading mb-[4px]">
              <TextInputLabel>상품 메모</TextInputLabel>
            </div>
            <div className="flex flex-col gap-[8px]">
              {createItemPayload.memos.length === 0 ? (
                <Button
                  onClick={handleAddMemo}
                  color={ButtonColor.CUSTOM_GRAY}
                  className="w-[32px]"
                >
                  +
                </Button>
              ) : (
                createItemPayload.memos.map((memo, index) => (
                  <MemoInput
                    key={index}
                    value={memo}
                    index={index}
                    onChange={handleMemoChange}
                    onRemove={handleRemoveMemo}
                    onAdd={handleAddMemo}
                    isMaxMemos={createItemPayload.memos.length >= 10}
                  />
                ))
              )}
            </div>
          </div>
        </div>
        <div className="flex gap-[16px]">
          <div className="flex flex-col">
            <TextInputLabel required>상품 설명</TextInputLabel>
            <CustomEditor
              content={createItemPayload.details}
              handleContentChange={(newContent) =>
                handleChange({
                  target: { name: "details", value: newContent },
                } as React.ChangeEvent<HTMLInputElement>)
              }
              style={{ width: "398px" }}
            />
            <Footnote12Description className="text-semantic-text-secondary mt-[12px]">{`(${getTextLength(
              createItemPayload.details
            )} / 1,000)`}</Footnote12Description>
          </div>
          <div className="flex flex-col">
            <TextInputLabel required>기능 설명</TextInputLabel>
            <CustomEditor
              content={createItemPayload.features}
              handleContentChange={(newContent) =>
                handleChange({
                  target: { name: "features", value: newContent },
                } as React.ChangeEvent<HTMLInputElement>)
              }
              style={{ width: "398px" }}
            />
            <Footnote12Description className="text-semantic-text-secondary mt-[12px]">{`(${getTextLength(
              createItemPayload.features
            )} / 1,000)`}</Footnote12Description>
          </div>
        </div>
      </div>
    </div>
  );
};
